const FOCUSABLE_SELECTOR =
  'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

export const isolateModalForAssistiveTechnology = (
  modalElement: HTMLElement | null,
) => {
  if (!modalElement || !document.body.contains(modalElement)) {
    return () => undefined;
  }

  const previousAriaHidden = new Map<HTMLElement, string | null>();

  let current: HTMLElement | null = modalElement;

  while (current && current !== document.body) {
    const parentElement = current.parentElement;

    if (!parentElement) break;

    Array.from(parentElement.children).forEach(sibling => {
      if (!(sibling instanceof HTMLElement) || sibling === current) {
        return;
      }

      if (!previousAriaHidden.has(sibling)) {
        previousAriaHidden.set(
          sibling,
          sibling.getAttribute('aria-hidden'),
        );
      }

      sibling.setAttribute('aria-hidden', 'true');
      sibling.setAttribute('inert', '');
    });

    current = parentElement;
  }

  return () => {
    previousAriaHidden.forEach((value, element) => {
      element.removeAttribute('inert');

      if (value === null) {
        element.removeAttribute('aria-hidden');
      } else {
        element.setAttribute('aria-hidden', value);
      }
    });
  };
};


export const getFocusableModalElements = (modalElement: HTMLElement | null) => {
  if (!modalElement) {
    return [] as HTMLElement[];
  }

  return Array.from(modalElement.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(element => {
    if (element.hasAttribute('disabled')) return false;
    if (element.getAttribute('aria-hidden') === 'true') return false;
    return true;
  });
};

export const focusModalContent = (
  modalElement: HTMLElement | null,
  preferredElement?: HTMLElement | null,
) => {
  if (preferredElement && !preferredElement.hasAttribute('disabled')) {
    preferredElement.focus();
    return;
  }

  const focusableElements = getFocusableModalElements(modalElement);

  if (focusableElements.length > 0) {
    focusableElements[0].focus();
    return;
  }

  modalElement?.focus();
};


export const containModalTabFocus = (event: KeyboardEvent, modalElement: HTMLElement | null) => {
  if (event.key !== 'Tab' || !modalElement) return;

  const focusableElements = getFocusableModalElements(modalElement);
  if (focusableElements.length === 0) {
    event.preventDefault();
    modalElement.focus();
    return;
  }

  const firstElement = focusableElements[0];
  const lastElement = focusableElements[focusableElements.length - 1];

  if (event.shiftKey && document.activeElement === firstElement) {
    event.preventDefault();
    lastElement.focus();
    return;
  }

  if (!event.shiftKey && document.activeElement === lastElement) {
    event.preventDefault();
    firstElement.focus();
  }
};